import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChartPieSlice, ChartBar, ChartLine, Gauge, Timer, Anchor,
  Truck, Fire, Wrench, Lightning, Drop, Trophy, MapPin, ArrowUp,
  ArrowDown, TrendUp, Warning as WarningI, Target,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SectionHeader from "@/components/SectionHeader";

/**
 * OperationsDashboardPbi — Dashboard operacional multi-vista tipo Power BI.
 * Filtros de fecha arriba. Widgets por categoría (Producción · Mantenimiento
 * · Consumo · Rankings). El cumplimiento vs budget se prorratea según la
 * unidad de tiempo del rango (día = mes*días/30; año = mes*12).
 */

const RANGE_PRESETS = [
  { v: "today", l: "Hoy" },
  { v: "week", l: "Últ. 7 días" },
  { v: "month", l: "Mes actual" },
  { v: "ytd", l: "YTD" },
  { v: "year", l: "Año actual" },
  { v: "custom", l: "Personalizado" },
];

const EQ_LABELS = {
  jumbo: "Jumbo", anclador: "Anclador", voladuras: "Voladuras",
  scoop: "Scoop", camion: "Camión BP", servicios: "Servicios",
  telehandler: "Telehandler", retro: "Retro", motoniv: "Motoniveladora",
};

const KPI_COLORS = {
  produccion: "#0F766E", mantto: "#B45309", consumo: "#7C3AED", ranking: "#1D4ED8",
};

const DELAY_COLORS = ["#DC2626", "#EA580C", "#D97706", "#CA8A04", "#65A30D", "#0891B2", "#4F46E5", "#7C3AED", "#DB2777", "#0F766E"];

function KpiCard({ icon: Icon, label, value, unit, hint, tone = "slate", trend, testid }) {
  const toneCls = {
    slate: "border-slate-200 bg-white text-slate-900",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
    red: "border-red-200 bg-red-50/60 text-red-900",
    blue: "border-blue-200 bg-blue-50/60 text-blue-900",
    purple: "border-purple-200 bg-purple-50/60 text-purple-900",
  }[tone] || "border-slate-200 bg-white text-slate-900";
  return (
    <div className={`border rounded-lg p-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1">
          {Icon && <Icon size={11} weight="bold" />} {label}
        </p>
        {trend !== undefined && trend !== null && (
          <span className={`text-[10px] font-bold inline-flex items-center gap-0.5 ${trend >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {trend >= 0 ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-black tabular-nums leading-tight mt-1">
        {typeof value === "number" ? value.toLocaleString("es-MX", { maximumFractionDigits: 2 }) : (value ?? "—")}
        {unit && <span className="text-xs font-bold text-slate-500 ml-1">{unit}</span>}
      </p>
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

/** Convierte el preset a rango [from, to] ISO date. */
function presetRange(preset, custom) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "custom") return custom;
  if (preset === "today") return { from: fmt(today), to: fmt(today) };
  if (preset === "week") {
    const w = new Date(today); w.setDate(w.getDate() - 6);
    return { from: fmt(w), to: fmt(today) };
  }
  if (preset === "month") {
    const m = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: fmt(m), to: fmt(today) };
  }
  if (preset === "ytd" || preset === "year") {
    const y = new Date(today.getFullYear(), 0, 1);
    const end = preset === "ytd" ? today : new Date(today.getFullYear(), 11, 31);
    return { from: fmt(y), to: fmt(end) };
  }
  return { from: fmt(today), to: fmt(today) };
}

/**
 * Prorratea el budget mensual al número de días efectivos del rango.
 * Estrategia: para cada mes tocado por el rango se toma la fracción de días
 * dentro del rango sobre los días del mes → esa fracción del budget mensual
 * aporta al target del rango.
 */
function prorateBudget(monthsBudget, fromISO, toISO) {
  if (!monthsBudget || !fromISO || !toISO) return {};
  const from = new Date(fromISO); const to = new Date(toISO);
  const result = {};
  for (let d = new Date(from); d <= to; d.setMonth(d.getMonth() + 1, 1)) {
    const yy = d.getFullYear(); const mm = d.getMonth() + 1;
    const mstart = new Date(yy, mm - 1, 1);
    const mend = new Date(yy, mm, 0); // último día del mes
    const eff_from = mstart < from ? from : mstart;
    const eff_to = mend > to ? to : mend;
    if (eff_from > eff_to) continue;
    const days_in = Math.round((eff_to - eff_from) / 86400000) + 1;
    const days_month = mend.getDate();
    const frac = days_in / days_month;
    const key = mm;
    const m_budget = monthsBudget.find((b) => b.month === key);
    if (!m_budget) continue;
    Object.entries(m_budget.budget || {}).forEach(([k, v]) => {
      result[k] = (result[k] || 0) + (Number(v) || 0) * frac;
    });
  }
  Object.keys(result).forEach((k) => { result[k] = Math.round(result[k] * 100) / 100; });
  return result;
}

/** Extrae Pareto de demoras desde operations_entries.metrics.demoras[]. */
async function fetchDelaysPareto(from, to) {
  try {
    const r = await api.get(`/operations/entries?date_from=${from}&date_to=${to}&limit=5000`);
    const entries = r.data || [];
    const bucket = {};
    entries.forEach((e) => {
      const list = e.metrics?.demoras || [];
      list.forEach((d) => {
        if (!d?.inicio || !d?.fin) return;
        const [h1, m1] = d.inicio.split(":").map(Number);
        const [h2, m2] = d.fin.split(":").map(Number);
        let dur = (h2 * 60 + m2) - (h1 * 60 + m1);
        if (dur < 0) dur += 1440;
        const cat = (d.motivo || d.categoria || "Sin categoría").trim();
        if (!bucket[cat]) bucket[cat] = { motivo: cat, horas: 0, count: 0 };
        bucket[cat].horas += dur / 60;
        bucket[cat].count += 1;
      });
      // legacy inline demora
      if (e.metrics?.demora_inicio && e.metrics?.demora_fin) {
        const [h1, m1] = e.metrics.demora_inicio.split(":").map(Number);
        const [h2, m2] = e.metrics.demora_fin.split(":").map(Number);
        let dur = (h2 * 60 + m2) - (h1 * 60 + m1);
        if (dur < 0) dur += 1440;
        const cat = (e.metrics.demora_motivo || "Sin categoría").trim();
        if (!bucket[cat]) bucket[cat] = { motivo: cat, horas: 0, count: 0 };
        bucket[cat].horas += dur / 60;
        bucket[cat].count += 1;
      }
    });
    const arr = Object.values(bucket)
      .map((b) => ({ ...b, horas: Math.round(b.horas * 100) / 100 }))
      .sort((a, b) => b.horas - a.horas);
    // Acumulado % para Pareto
    const total = arr.reduce((s, b) => s + b.horas, 0);
    let acc = 0;
    return arr.map((b) => {
      acc += b.horas;
      return { ...b, acc_pct: total > 0 ? Math.round(acc / total * 1000) / 10 : 0 };
    });
  } catch { return []; }
}

export default function OperationsDashboardPbi() {
  const [preset, setPreset] = useState("month");
  const now = new Date();
  const [custom, setCustom] = useState({
    from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  });
  const range = useMemo(() => presetRange(preset, custom), [preset, custom]);
  const [loading, setLoading] = useState(false);
  const [opsKpis, setOpsKpis] = useState(null);
  const [budgetDash, setBudgetDash] = useState(null);
  const [reliability, setReliability] = useState(null);
  const [maintKpis, setMaintKpis] = useState(null);
  const [pareto, setPareto] = useState([]);

  const load = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    try {
      const yy = new Date(range.from).getFullYear();
      const [ops, bud, rel, mnt, par] = await Promise.all([
        api.get(`/operations/kpis?date_from=${range.from}&date_to=${range.to}`).then((r) => r.data),
        api.get(`/operations/budget/dashboard?year=${yy}`).then((r) => r.data).catch(() => null),
        api.get(`/maintenance/reliability`).then((r) => r.data).catch(() => null),
        api.get(`/maintenance/kpis?date_from=${range.from}&date_to=${range.to}`).then((r) => r.data).catch(() => null),
        fetchDelaysPareto(range.from, range.to),
      ]);
      setOpsKpis(ops); setBudgetDash(bud); setReliability(rel); setMaintKpis(mnt); setPareto(par);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  // Cumplimiento vs budget prorrateado
  const prorated = useMemo(() => prorateBudget(budgetDash?.months || [], range.from, range.to), [budgetDash, range.from, range.to]);
  const byEq = opsKpis?.by_equipment || {};

  // Agregados de producción (todos los equipos)
  const prod = useMemo(() => {
    const j = byEq.jumbo?.totals || {};
    const anc = byEq.anclador?.totals || {};
    const vol = byEq.voladuras?.totals || {};
    const sc = byEq.scoop?.totals || {};
    const cm = byEq.camion?.totals || {};
    const sv = byEq.servicios?.totals || {};
    const mangas = Number(sv.metros_mangas || 0);
    const servmi = Number(sv.metros_instalados || 0);
    const total_meters = Number(j.metros || 0) + Number(j.avance_m || 0) + Number(anc.metros || 0) + Number(anc.metros_instalados || 0);
    const eff_pct = prorated.meters_total > 0 ? Math.round(total_meters / prorated.meters_total * 1000) / 10 : null;
    return {
      total_meters, jumbo_m: Number(j.metros || 0) + Number(j.avance_m || 0),
      pernos: Number(anc.pernos || 0), malla_m2: Number(anc.malla_m2 || 0),
      barrenos: Number(j.barrenos || 0) + Number(anc.barrenos || 0),
      explosivo_kg: Number(vol.explosivo_kg || 0), avance_vol: Number(vol.avance_m || 0),
      ton_mineral: Number((byEq.scoop?.totals?.ton_mineral) || 0) + Number((byEq.camion?.totals?.ton_mineral) || 0),
      ton_scoop: Number(sc.toneladas || 0), ton_camion: Number(cm.toneladas || 0),
      mangas, servmi,
      kg_por_m: (Number(vol.explosivo_kg || 0) > 0 && Number(vol.avance_m || 0) > 0)
        ? Math.round(Number(vol.explosivo_kg) / Number(vol.avance_m) * 100) / 100 : 0,
      eff_pct,
    };
  }, [byEq, prorated]);

  // Utilización & horas por equipo
  const util = useMemo(() => {
    const rows = [];
    Object.entries(byEq).forEach(([eq, k]) => {
      const hef = k.hours_efectivas || 0;
      const hp = k.hours_percusion || 0;
      const util_pct = eq === "jumbo" && hef > 0 ? Math.round(hp / hef * 1000) / 10 : (k.availability_pct || 0);
      rows.push({
        eq: EQ_LABELS[eq] || eq, key: eq,
        hours_ef: hef, hours_perc: hp, availability: k.availability_pct || 0,
        util_pct, diesel_lt: k.diesel_lt || 0, entries: k.entries || 0,
      });
    });
    return rows.sort((a, b) => b.hours_ef - a.hours_ef);
  }, [byEq]);

  // Diésel total consumido (todos los equipos)
  const diesel_total = util.reduce((s, r) => s + (r.diesel_lt || 0), 0);

  return (
    <div className="space-y-4" data-testid="ops-dashboard-pbi">
      <SectionHeader icon={ChartPieSlice} title="Dashboard Operacional" eyebrow="Multi-vista · Real vs Budget prorrateado" size="lg" testid="ops-dash-header"
        right={
          <>
            <div>
              <Label className="overline-label">Rango</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger data-testid="ops-dash-preset" className="h-9 w-40"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {RANGE_PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label">Desde</Label>
              <Input type="date" value={range.from} disabled={preset !== "custom"}
                     onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                     data-testid="ops-dash-from" className="h-9 w-36" />
            </div>
            <div>
              <Label className="overline-label">Hasta</Label>
              <Input type="date" value={range.to} disabled={preset !== "custom"}
                     onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                     data-testid="ops-dash-to" className="h-9 w-36" />
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ops-dash-refresh">
              {loading ? "…" : "Actualizar"}
            </Button>
          </>
        } />

      {/* Banner cumplimiento vs budget */}
      {prorated.meters_total > 0 && (
        <div className="grid md:grid-cols-4 gap-2" data-testid="ops-dash-compliance">
          <KpiCard icon={Target} label="Meta metros (prorrateada)" value={Math.round(prorated.meters_total)} unit="m"
                   hint={`${range.from} → ${range.to}`} tone="blue" testid="ops-dash-target" />
          <KpiCard icon={TrendUp} label="Real ejecutado" value={Math.round(prod.total_meters)} unit="m" tone="emerald" testid="ops-dash-real" />
          <KpiCard icon={Gauge} label="% Cumplimiento" value={prod.eff_pct ?? "—"} unit="%"
                   tone={(prod.eff_pct ?? 0) >= 90 ? "emerald" : (prod.eff_pct ?? 0) >= 70 ? "amber" : "red"}
                   testid="ops-dash-fulfill" />
          <KpiCard icon={Drop} label="Diésel total" value={Math.round(diesel_total)} unit="L" tone="purple" testid="ops-dash-diesel" />
        </div>
      )}

      <Tabs defaultValue="produccion">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="produccion" data-testid="ops-dash-tab-prod" className="text-xs">
            🏗️ Producción
          </TabsTrigger>
          <TabsTrigger value="mantto" data-testid="ops-dash-tab-mantto" className="text-xs">
            🔧 Mantenimiento
          </TabsTrigger>
          <TabsTrigger value="consumo" data-testid="ops-dash-tab-consumo" className="text-xs">
            ⛽ Consumo
          </TabsTrigger>
          <TabsTrigger value="rankings" data-testid="ops-dash-tab-rank" className="text-xs">
            🏆 Rankings
          </TabsTrigger>
        </TabsList>

        {/* ===== PRODUCCIÓN ===== */}
        <TabsContent value="produccion" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <KpiCard icon={Anchor} label="Pernos instalados" value={prod.pernos} unit="pz" tone="blue" testid="prod-pernos" />
            <KpiCard icon={Anchor} label="Malla instalada" value={prod.malla_m2} unit="m²" testid="prod-malla" />
            <KpiCard icon={Wrench} label="Barrenos" value={prod.barrenos} unit="pz" testid="prod-barrenos" />
            <KpiCard icon={Fire} label="Explosivo" value={prod.explosivo_kg} unit="kg" tone="red" testid="prod-expl" />
            <KpiCard icon={Fire} label="Factor de carga" value={prod.kg_por_m} unit="kg/m" hint="explosivo ÷ avance" tone="amber" testid="prod-carga" />
            <KpiCard icon={Truck} label="Ton Scoop" value={prod.ton_scoop} unit="t" tone="emerald" testid="prod-ton-scoop" />
            <KpiCard icon={Truck} label="Ton Camión BP" value={prod.ton_camion} unit="t" tone="emerald" testid="prod-ton-camion" />
            <KpiCard icon={Lightning} label="Metros mangas" value={prod.mangas} unit="m" testid="prod-mangas" />
            <KpiCard icon={Lightning} label="Metros servicios" value={prod.servmi} unit="m" testid="prod-servmi" />
            <KpiCard icon={Timer} label="Horas Motoniv" value={byEq.motoniv?.hours_efectivas || 0} unit="h" testid="prod-h-motoniv" />
            <KpiCard icon={Timer} label="Horas Retro" value={byEq.retro?.hours_efectivas || 0} unit="h" testid="prod-h-retro" />
            <KpiCard icon={Timer} label="Horas Telehandler" value={byEq.telehandler?.hours_efectivas || 0} unit="h" testid="prod-h-tele" />
          </div>

          {/* Real vs Budget (prorrateado) por métrica */}
          {Object.keys(prorated).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3 flex items-center gap-1">
                <Target size={12} weight="bold" /> Cumplimiento vs Budget prorrateado
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: "Metros total", real: prod.total_meters, meta: prorated.meters_total || 0 },
                    { name: "Ton mineral", real: prod.ton_mineral || (prod.ton_scoop + prod.ton_camion), meta: prorated.ton_mineral || 0 },
                    { name: "Ton tepetate", real: 0, meta: prorated.ton_tepetate || 0 },
                    { name: "Camión t", real: prod.ton_camion, meta: prorated.truck_tons || 0 },
                    { name: "Scoop t", real: prod.ton_scoop, meta: prorated.scoop_tons || 0 },
                  ].filter((r) => r.meta > 0 || r.real > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="meta" name="Budget prorrateado" fill="#94A3B8" />
                    <Bar dataKey="real" name="Real ejecutado" fill={KPI_COLORS.produccion} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== MANTENIMIENTO ===== */}
        <TabsContent value="mantto" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="mantto-kpis">
            <KpiCard icon={Timer} label="MTBF (fleet)" value={reliability?.fleet?.mtbf_h ?? "—"} unit="h"
                     hint="Mean Time Between Failures" tone="blue" testid="mantto-mtbf" />
            <KpiCard icon={Wrench} label="MTTR (fleet)" value={reliability?.fleet?.mttr_h ?? "—"} unit="h"
                     hint="Mean Time To Repair" tone="amber" testid="mantto-mttr" />
            <KpiCard icon={Gauge} label="Confiabilidad" value={reliability?.fleet?.reliability_pct ?? "—"} unit="%"
                     hint="MTBF / (MTBF + MTTR)" tone="emerald" testid="mantto-rel" />
            <KpiCard icon={WarningI} label="Horas mantto" value={maintKpis?.total_hours ?? maintKpis?.hours_total ?? "—"} unit="h"
                     tone="red" testid="mantto-hours" />
          </div>

          {reliability?.equipment && reliability.equipment.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 px-3 pt-3">Confiabilidad por equipo</p>
              <table className="w-full text-xs mt-2" data-testid="mantto-eq-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="text-left px-3 py-1.5">Equipo</th>
                    <th className="text-right px-3 py-1.5">MTBF (h)</th>
                    <th className="text-right px-3 py-1.5">MTTR (h)</th>
                    <th className="text-right px-3 py-1.5">Confiab. %</th>
                    <th className="text-right px-3 py-1.5">Fallas</th>
                  </tr>
                </thead>
                <tbody>
                  {reliability.equipment.slice(0, 20).map((e) => (
                    <tr key={e.id || e.code} className="border-b border-slate-100">
                      <td className="px-3 py-1 font-semibold">{e.code || e.name}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{e.mtbf_h ?? "—"}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{e.mttr_h ?? "—"}</td>
                      <td className={`px-3 py-1 text-right tabular-nums font-bold ${(e.reliability_pct ?? 0) >= 90 ? "text-emerald-700" : (e.reliability_pct ?? 0) >= 75 ? "text-amber-700" : "text-red-700"}`}>
                        {e.reliability_pct ?? "—"}
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums">{e.failures ?? e.n ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ===== CONSUMO / PARETO DEMORAS ===== */}
        <TabsContent value="consumo" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiCard icon={Drop} label="Diésel total" value={Math.round(diesel_total)} unit="L" tone="purple" testid="consumo-diesel-total" />
            <KpiCard icon={Drop} label="Diésel · Camión" value={Math.round(byEq.camion?.diesel_lt || 0)} unit="L" testid="consumo-d-camion" />
            <KpiCard icon={Drop} label="Diésel · Scoop" value={Math.round(byEq.scoop?.diesel_lt || 0)} unit="L" testid="consumo-d-scoop" />
            <KpiCard icon={Drop} label="Diésel · Aux" value={Math.round((byEq.telehandler?.diesel_lt || 0) + (byEq.retro?.diesel_lt || 0) + (byEq.motoniv?.diesel_lt || 0))} unit="L" testid="consumo-d-aux" />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
              <ChartBar size={12} weight="bold" /> Pareto de demoras
              <span className="text-[10px] text-slate-400 font-normal ml-2">({pareto.length} motivos)</span>
            </p>
            {pareto.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-6">Sin demoras registradas en el rango.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pareto.slice(0, 10)} margin={{ top: 6, right: 30, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="motivo" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: "Horas", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: "% Acum.", angle: 90, position: "insideRight", style: { fontSize: 10 } }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="left" dataKey="horas" name="Horas de demora">
                      {pareto.slice(0, 10).map((_, i) => (
                        <Cell key={i} fill={DELAY_COLORS[i % DELAY_COLORS.length]} />
                      ))}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="acc_pct" name="% Acumulado" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== RANKINGS ===== */}
        <TabsContent value="rankings" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
              <ChartLine size={12} weight="bold" /> Utilización por equipo (horas efectivas · disponibilidad %)
            </p>
            <div className="h-72" data-testid="util-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={util}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="eq" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: "Horas", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} label={{ value: "%", angle: 90, position: "insideRight", style: { fontSize: 10 } }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="hours_ef" name="Horas efectivas" fill={KPI_COLORS.produccion} />
                  <Line yAxisId="right" type="monotone" dataKey="util_pct" name="Utilización %" stroke={KPI_COLORS.ranking} strokeWidth={2} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-500 italic mt-1">
              Utilización · Jumbo: h percusión / h efectiva. Otros equipos: disponibilidad (efectiva / (efectiva + demora)).
            </p>
          </div>

          {/* Top operadores por equipo */}
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(byEq).filter(([, k]) => (k.operator_ranking || []).length > 0).slice(0, 4).map(([eq, k]) => (
              <div key={eq} className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                  <Trophy size={12} weight="bold" className="text-amber-600" /> Top operadores · {EQ_LABELS[eq] || eq}
                </p>
                <table className="w-full text-xs">
                  <tbody>
                    {k.operator_ranking.slice(0, 5).map((op, i) => (
                      <tr key={op.operator_id} className="border-t border-slate-100">
                        <td className="py-1 text-slate-400 w-6 font-bold">{i + 1}</td>
                        <td className="py-1 font-semibold text-slate-800 truncate">{op.name}</td>
                        <td className="py-1 text-right tabular-nums font-black">{op.primary.toLocaleString("es-MX", { maximumFractionDigits: 2 })}</td>
                        <td className="py-1 text-right tabular-nums text-slate-500 w-14">{op.hours_ef}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Top áreas */}
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(byEq).filter(([, k]) => (k.area_ranking || []).length > 0).slice(0, 4).map(([eq, k]) => (
              <div key={eq} className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                  <MapPin size={12} weight="bold" className="text-blue-700" /> Top áreas · {EQ_LABELS[eq] || eq}
                </p>
                <table className="w-full text-xs">
                  <tbody>
                    {k.area_ranking.slice(0, 5).map((ar, i) => (
                      <tr key={ar.area} className="border-t border-slate-100">
                        <td className="py-1 text-slate-400 w-6 font-bold">{i + 1}</td>
                        <td className="py-1 font-semibold text-slate-800 truncate">{ar.area}</td>
                        <td className="py-1 text-right tabular-nums font-black">{ar.primary.toLocaleString("es-MX", { maximumFractionDigits: 2 })}</td>
                        <td className="py-1 text-right tabular-nums text-slate-500 w-14">{ar.hours_ef}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <p className="text-[10px] text-slate-400 italic text-center">
        Fuentes: operations_entries · operations_budgets · maintenance_failures · maintenance/reliability · maintenance/kpis. Budget prorrateado prorateando plan mensual sobre días efectivos del rango.
      </p>
    </div>
  );
}
